import { renderLucideIcon } from '../utils/lucide-icons.js'

function normalizeDictationWritingPlanUi(result) {
  if (!result || typeof result !== 'object') return null
  var ui = result.ui || result.dictationWritingPlanUi || null
  if (!ui || typeof ui !== 'object') return null
  if (String(ui.type || '').toLowerCase() !== 'dictation-writing-plan') return null

  var operations = Array.isArray(ui.operations)
    ? ui.operations.map(normalizeOperation).filter(Boolean)
    : []
  if (!operations.length) return null

  return {
    type: 'dictation-writing-plan',
    title: String(ui.title || 'Plan d’écriture à valider'),
    message: String(ui.message || '').trim(),
    operations: operations,
    relations: Array.isArray(ui.relations) ? ui.relations.map(normalizeRelation).filter(Boolean) : [],
    provenance: normalizeProvenance(ui.provenance),
    labels: normalizeLabels(ui.labels),
    nextStepKey: String(ui.nextStepKey || 'DictationExecute')
  }
}

function renderDictationWritingPlanUi(ui, options) {
  options = options || {}
  var readOnly = options.readOnly === true || ui.readOnly === true
  var selectedCount = ui.operations.filter(function (operation) { return operation.selected }).length
  return (
    '<div class="wc-dictation-writing-plan' + (readOnly ? ' is-readonly' : '') + '" data-ds-view="dictation-writing-plan" data-dictation-writing-plan-readonly="' + (readOnly ? 'true' : 'false') + '">' +
      '<div class="wc-ds-capability wc-ds-capability-dictation">' +
        '<div class="wc-ds-capability-name">' + renderLucideIcon('mic') + '<span>' + escapeHtml(ui.labels.capability) + '</span></div>' +
      '</div>' +
      '<form class="wc-dictation-writing-plan-form">' +
        '<div class="wc-dictation-writing-plan-head">' +
          '<div class="wc-dictation-writing-plan-title">' + escapeHtml(formatOperationCount(ui.operations.length)) + '</div>' +
          '<div class="wc-dictation-writing-plan-message">' + escapeHtml(ui.message || 'Sélectionne les actions à effectuer. Aucune écriture n’a encore été réalisée.') + '</div>' +
        '</div>' +
        '<div class="wc-dictation-writing-plan-list">' +
          ui.operations.map(function (operation) { return renderOperation(operation, readOnly) }).join('') +
        '</div>' +
        renderDependencyWarning(ui) +
        renderProvenance(ui) +
        (readOnly ? '' : '<div class="wc-dictation-writing-plan-actions">' +
          '<span class="wc-dictation-writing-plan-count" data-dictation-writing-plan-count>' + escapeHtml(formatSelectedCount(selectedCount)) + '</span>' +
          '<div class="wc-dictation-writing-plan-buttons">' +
            '<button type="button" class="wc-dictation-writing-plan-cancel" data-dictation-writing-plan-cancel>' + renderLucideIcon('x') + '<span>' + escapeHtml(ui.labels.cancel) + '</span></button>' +
            '<button type="submit" class="wc-dictation-writing-plan-confirm" data-dictation-writing-plan-confirm>' + renderLucideIcon('check') + '<span>' + escapeHtml(formatConfirmLabel(selectedCount)) + '</span></button>' +
          '</div>' +
        '</div>') +
      '</form>' +
    '</div>'
  )
}

function bindDictationWritingPlanUi(el, ui, handlers) {
  if (!el || !ui) return
  var form = el.querySelector('.wc-dictation-writing-plan-form')
  if (!form) return

  form.addEventListener('submit', function (event) {
    event.preventDefault()
    refreshState(el, ui)
    var confirm = el.querySelector('[data-dictation-writing-plan-confirm]')
    if (confirm && confirm.disabled) return
    if (!handlers || typeof handlers.onConfirm !== 'function') return
    handlers.onConfirm(buildPayload(el, ui, 'confirm'))
  })

  var cancel = el.querySelector('[data-dictation-writing-plan-cancel]')
  if (cancel) {
    cancel.addEventListener('click', function () {
      if (!handlers || typeof handlers.onCancel !== 'function') return
      handlers.onCancel(buildPayload(el, ui, 'cancel'))
    })
  }

  el.querySelectorAll('[data-dictation-operation-selected]').forEach(function (input) {
    input.addEventListener('change', function () { refreshState(el, ui) })
  })
  el.querySelectorAll('[data-dictation-operation-edit]').forEach(function (button) {
    button.addEventListener('click', function () {
      var row = button.closest('[data-dictation-operation-id]')
      if (!row) return
      row.classList.toggle('is-editing')
      refreshState(el, ui)
    })
  })
  el.querySelectorAll('[data-dictation-operation-save]').forEach(function (button) {
    button.addEventListener('click', function () {
      var row = button.closest('[data-dictation-operation-id]')
      if (!row) return
      row.classList.remove('is-editing')
      refreshState(el, ui)
    })
  })
  el.querySelectorAll('[data-dictation-select-dependency]').forEach(function (button) {
    button.addEventListener('click', function () {
      var operationId = button.getAttribute('data-dictation-select-dependency') || ''
      var checkbox = el.querySelector('[data-dictation-operation-selected="' + cssEscape(operationId) + '"]')
      if (checkbox) checkbox.checked = true
      refreshState(el, ui)
    })
  })

  refreshState(el, ui)
}

function buildPayload(el, ui, action) {
  var selectedOperationIds = ui.operations
    .filter(function (operation) { return readOperationSelected(el, operation) })
    .map(function (operation) { return operation.id })
  return {
    nextStepKey: ui.nextStepKey || 'DictationExecute',
    label: action === 'cancel' ? ui.labels.cancel : ui.labels.confirm,
    action: action,
    allOperationIds: ui.operations.map(function (operation) { return operation.id }),
    selectedOperationIds: selectedOperationIds,
    operations: ui.operations.map(function (operation) {
      var selected = readOperationSelected(el, operation)
      return {
        id: operation.id,
        kind: operation.kind,
        objectType: operation.objectType,
        selected: selected,
        fields: selected
          ? operation.fields.map(function (field) {
            return {
              path: field.path,
              proposedValue: readFieldValue(el, operation.id, field)
            }
          })
          : []
      }
    }).filter(function (operation) { return action !== 'confirm' || operation.selected })
  }
}

function renderOperation(operation, readOnly) {
  var summary = normalizeVisibleSummary(operation)
  return (
    '<div class="wc-dictation-writing-plan-operation' + (!operation.selected ? ' is-off' : '') + '" data-dictation-operation-id="' + escapeAttributeValue(operation.id) + '">' +
      '<div class="wc-dictation-writing-plan-operation-row">' +
        '<input class="wc-dictation-writing-plan-check" type="checkbox" data-dictation-operation-selected="' + escapeAttributeValue(operation.id) + '"' + (operation.selected ? ' checked' : '') + (readOnly ? ' disabled aria-disabled="true"' : '') + ' aria-label="Sélectionner ' + escapeAttributeValue(operation.label) + '">' +
        '<span class="wc-dictation-writing-plan-glyph">' + renderLucideIcon(operation.icon || iconForObjectType(operation.objectType)) + '</span>' +
        '<div class="wc-dictation-writing-plan-operation-body">' +
          '<div class="wc-dictation-writing-plan-operation-kind">' + escapeHtml(operation.kind === 'create' ? formatCreateKind(operation.objectType) : 'Modifier') + '</div>' +
          '<div class="wc-dictation-writing-plan-operation-label">' + escapeHtml(operation.label) + '</div>' +
          (summary ? '<div class="wc-dictation-writing-plan-summary">' + escapeHtml(formatDisplayValue(summary, operation.fields)) + '</div>' : '') +
          renderOperationRelations(operation) +
        '</div>' +
        (readOnly ? '' : '<button type="button" class="wc-dictation-writing-plan-edit" data-dictation-operation-edit>' + renderLucideIcon('pencil') + '<span>Modifier</span></button>') +
      '</div>' +
      renderOperationEdit(operation, readOnly) +
    '</div>'
  )
}

function normalizeVisibleSummary(operation) {
  var summary = String(operation.summary || '').trim()
  var label = String(operation.label || '').trim()
  return summary && summary !== label ? summary : ''
}

function renderOperationEdit(operation, readOnly) {
  if (!operation.fields.length) return ''
  return (
    '<div class="wc-dictation-writing-plan-edit-panel">' +
      '<div class="wc-dictation-writing-plan-fields">' +
        operation.fields.map(function (field) { return renderField(operation, field, readOnly) }).join('') +
      '</div>' +
      (readOnly ? '' : '<button type="button" class="wc-dictation-writing-plan-save" data-dictation-operation-save>' + renderLucideIcon('check') + '<span>Enregistrer les modifications</span></button>') +
    '</div>'
  )
}

function renderField(operation, field, readOnly) {
  var disabled = readOnly || !field.editable
  return (
    '<label class="wc-dictation-writing-plan-field" data-dictation-input-type="' + escapeAttributeValue(field.inputType) + '">' +
      '<span class="wc-dictation-writing-plan-label-row"><span>' + escapeHtml(field.label) + (field.required ? ' *' : '') + '</span>' + renderSourceBadge(field.source) + '</span>' +
      renderFieldControl(operation, field, disabled) +
      (field.justification ? '<span class="wc-dictation-writing-plan-help">' + escapeHtml(field.justification) + '</span>' : '') +
    '</label>'
  )
}

function renderFieldControl(operation, field, disabled) {
  var common = ' data-dictation-operation-id="' + escapeAttributeValue(operation.id) + '" data-dictation-field-path="' + escapeAttributeValue(field.path) + '"' + (disabled ? ' disabled aria-disabled="true"' : '')
  if (field.inputType === 'select') {
    return '<select' + common + '>' + field.options.map(function (option) {
      return '<option value="' + escapeAttributeValue(option.value) + '"' + (String(option.value) === String(field.proposedValue) ? ' selected' : '') + '>' + escapeHtml(option.label) + '</option>'
    }).join('') + '</select>'
  }
  if (field.inputType === 'textarea') {
    return '<textarea rows="4"' + common + '>' + escapeHtml(field.proposedValue || '') + '</textarea>'
  }
  if (field.inputType === 'checkbox') {
    return '<input type="checkbox"' + common + (normalizeBoolean(field.proposedValue) ? ' checked' : '') + '>'
  }
  var value = field.inputType === 'datetime-local'
    ? formatDateTimeLocal(field.proposedValue)
    : field.inputType === 'date'
      ? formatDateValue(field.proposedValue)
      : field.proposedValue || ''
  if (field.inputType === 'date') {
    return '<input type="text" inputmode="numeric" placeholder="DD/MM/YYYY" pattern="\\d{2}/\\d{2}/\\d{4}" title="Format attendu : DD/MM/YYYY"' + common + ' value="' + escapeAttributeValue(value) + '">'
  }
  return '<input type="' + escapeAttributeValue(field.inputType || 'text') + '"' + common + ' value="' + escapeAttributeValue(value) + '">'
}

function renderOperationRelations(operation) {
  if (!operation.relations.length) return ''
  return '<div class="wc-dictation-writing-plan-operation-relations">' + operation.relations.map(function (relation) {
    return '<div class="wc-dictation-writing-plan-relation">' + renderLucideIcon('cornerDownRight') + '<span>' + escapeHtml(relation.label) + ' <strong>' + escapeHtml(relation.targetLabel) + '</strong></span></div>'
  }).join('') + '</div>'
}

function renderDependencyWarning(ui) {
  var warnings = ui.operations.flatMap(function (operation) {
    return operation.dependencies.filter(function (dependency) { return dependency.required }).map(function (dependency) {
      return { operation: operation, dependency: dependency }
    })
  })
  if (!warnings.length) return ''
  return (
    '<div class="wc-dictation-writing-plan-warning" data-dictation-dependency-warning hidden>' +
      '<div class="wc-dictation-writing-plan-warning-title">' + renderLucideIcon('triangleAlert') + '<span>Dépendance à vérifier</span></div>' +
      warnings.map(function (warning) {
        return '<div class="wc-dictation-writing-plan-warning-item" data-dictation-dependency-item data-operation-id="' + escapeAttributeValue(warning.operation.id) + '" data-dependency-id="' + escapeAttributeValue(warning.dependency.operationId) + '">' +
          '<span>' + escapeHtml(warning.operation.label) + ' dépend de ' + escapeHtml(warning.dependency.label) + '.</span>' +
          '<button type="button" class="wc-dictation-writing-plan-secondary" data-dictation-select-dependency="' + escapeAttributeValue(warning.dependency.operationId) + '">Sélectionner aussi</button>' +
        '</div>'
      }).join('') +
    '</div>'
  )
}

function renderProvenance(ui) {
  var uncertainties = ui.provenance.uncertainties
  if (!uncertainties.length) return ''
  return (
    '<div class="wc-dictation-writing-plan-uncertainties">' +
      '<div class="wc-dictation-writing-plan-section-title">' + renderLucideIcon('triangleAlert') + '<span>Points à vérifier</span></div>' +
      '<ul>' + uncertainties.map(function (item) { return '<li>' + escapeHtml(item) + '</li>' }).join('') + '</ul>' +
    '</div>'
  )
}

function refreshState(el, ui) {
  var selectedCount = 0
  var hasInvalidDependency = false
  var hasOpenEdit = Boolean(el.querySelector('.wc-dictation-writing-plan-operation.is-editing'))
  ui.operations.forEach(function (operation) {
    var row = el.querySelector('[data-dictation-operation-id="' + cssEscape(operation.id) + '"]')
    if (!row) return
    var selected = readOperationSelected(el, operation)
    if (selected) selectedCount += 1
    row.classList.toggle('is-off', !selected)
  })

  el.querySelectorAll('[data-dictation-dependency-item]').forEach(function (item) {
    var operationId = item.getAttribute('data-operation-id') || ''
    var dependencyId = item.getAttribute('data-dependency-id') || ''
    var operation = ui.operations.find(function (candidate) { return candidate.id === operationId })
    var dependency = ui.operations.find(function (candidate) { return candidate.id === dependencyId })
    var invalid = Boolean(operation && dependency && readOperationSelected(el, operation) && !readOperationSelected(el, dependency))
    item.hidden = !invalid
    if (invalid) hasInvalidDependency = true
  })

  var warning = el.querySelector('[data-dictation-dependency-warning]')
  if (warning) warning.hidden = !hasInvalidDependency
  var count = el.querySelector('[data-dictation-writing-plan-count]')
  if (count) count.textContent = formatSelectedCount(selectedCount)
  var confirm = el.querySelector('[data-dictation-writing-plan-confirm]')
  if (confirm) {
    confirm.disabled = selectedCount === 0 || hasInvalidDependency || hasOpenEdit
    var label = confirm.querySelector('span')
    if (label) label.textContent = formatConfirmLabel(selectedCount)
  }
}

function normalizeOperation(value) {
  if (!value || typeof value !== 'object') return null
  var id = String(value.id || '').trim()
  var kind = String(value.kind || '').trim()
  var objectType = String(value.objectType || '').trim()
  var fields = Array.isArray(value.fields) ? value.fields.map(normalizeField).filter(Boolean) : []
  if (!id || (kind !== 'create' && kind !== 'update') || !objectType || !fields.length) return null
  return {
    id: id,
    kind: kind,
    objectType: objectType,
    label: String(value.label || objectType).trim(),
    icon: String(value.icon || iconForObjectType(objectType)).trim(),
    selected: value.selected !== false,
    summary: String(value.summary || value.label || '').trim(),
    status: String(value.status || 'pending_validation').trim(),
    dependencies: Array.isArray(value.dependencies) ? value.dependencies.map(normalizeDependency).filter(Boolean) : [],
    relations: Array.isArray(value.relations) ? value.relations.map(normalizeRelation).filter(Boolean) : [],
    fields: fields
  }
}

function normalizeField(value) {
  if (!value || typeof value !== 'object') return null
  var path = String(value.path || '').trim()
  var label = String(value.label || path).trim()
  if (!path || !label) return null
  var inputType = normalizeInputType(value.inputType, path)
  var options = Array.isArray(value.options) ? value.options.map(normalizeOption).filter(Boolean) : []
  if (inputType === 'select' && !options.length) inputType = 'text'
  return {
    path: path,
    label: label,
    currentValue: String(value.currentValue || '—'),
    proposedValue: String(value.proposedValue || ''),
    inputType: inputType,
    options: options,
    required: value.required === true,
    source: String(value.source || 'deduced'),
    confidence: String(value.confidence || 'medium'),
    justification: value.justification ? String(value.justification) : null,
    editable: value.editable !== false
  }
}

function normalizeDependency(value) {
  if (!value || typeof value !== 'object') return null
  var operationId = String(value.operationId || '').trim()
  if (!operationId) return null
  return {
    operationId: operationId,
    label: String(value.label || operationId).trim(),
    required: value.required !== false
  }
}

function normalizeOption(value) {
  if (!value || typeof value !== 'object') return null
  var optionValue = String(value.value || '').trim()
  if (!optionValue) return null
  return {
    value: optionValue,
    label: String(value.label || optionValue).trim()
  }
}

function normalizeRelation(value) {
  if (!value || typeof value !== 'object') return null
  var label = String(value.label || '').trim()
  var targetLabel = String(value.targetLabel || value.recordId || '').trim()
  if (!label || !targetLabel) return null
  return {
    label: label,
    objectType: String(value.objectType || '').trim(),
    recordId: String(value.recordId || '').trim() || null,
    targetLabel: targetLabel,
    source: String(value.source || 'context')
  }
}

function normalizeProvenance(value) {
  value = value && typeof value === 'object' ? value : {}
  return {
    originCapability: String(value.originCapability || '').trim() || null,
    originStep: String(value.originStep || '').trim() || null,
    initialIntent: String(value.initialIntent || '').trim(),
    latestUserText: String(value.latestUserText || '').trim(),
    justifications: normalizeStringList(value.justifications),
    uncertainties: normalizeStringList(value.uncertainties)
  }
}

function normalizeLabels(value) {
  value = value && typeof value === 'object' ? value : {}
  return {
    capability: String(value.capability || '@Dictée'),
    operations: String(value.operations || 'Opérations'),
    relations: String(value.relations || 'Relations'),
    currentValue: String(value.currentValue || 'Valeur actuelle'),
    proposedValue: String(value.proposedValue || 'Nouvelle valeur'),
    confirm: String(value.confirm || 'Valider le plan'),
    cancel: String(value.cancel || 'Annuler')
  }
}

function normalizeStringList(value) {
  return Array.isArray(value)
    ? value.map(function (item) { return String(item || '').trim() }).filter(Boolean)
    : []
}

function readOperationSelected(el, operation) {
  var input = el.querySelector('[data-dictation-operation-selected="' + cssEscape(operation.id) + '"]')
  return input ? input.checked : operation.selected
}

function readFieldValue(el, operationId, field) {
  var input = el.querySelector('[data-dictation-operation-id="' + cssEscape(operationId) + '"][data-dictation-field-path="' + cssEscape(field.path) + '"]')
  if (!input) return field.proposedValue
  if (field.inputType === 'checkbox') return input.checked ? 'true' : 'false'
  if (field.inputType === 'datetime-local' && input.value) {
    var date = new Date(input.value)
    return Number.isNaN(date.getTime()) ? String(input.value || '').trim() : date.toISOString()
  }
  if (field.inputType === 'date') return parseDateValue(input.value)
  return String(input.value || '').trim()
}

function normalizeInputType(value, path) {
  var normalized = String(value || '').trim()
  if (['text', 'textarea', 'number', 'date', 'datetime-local', 'select', 'checkbox', 'email', 'tel', 'url'].includes(normalized)) return normalized
  if (/amount\.value|probability|count/i.test(path)) return 'number'
  if (/startAt|endAt/i.test(path)) return 'datetime-local'
  if (/type|currency|status/i.test(path)) return 'select'
  if (/notes|comment/i.test(path)) return 'textarea'
  return 'text'
}

function formatOperationCount(count) {
  return count + ' action' + (count > 1 ? 's' : '') + ' proposée' + (count > 1 ? 's' : '')
}

function formatSelectedCount(count) {
  return count + ' action' + (count > 1 ? 's' : '') + ' sélectionnée' + (count > 1 ? 's' : '')
}

function formatConfirmLabel(count) {
  if (count <= 1) return 'Confirmer l’action'
  return 'Confirmer les ' + count + ' actions'
}

function formatCreateKind(objectType) {
  if (objectType === 'contact') return 'Créer un contact'
  if (objectType === 'entity') return 'Créer un compte'
  if (objectType === 'deal') return 'Créer une opportunité'
  if (objectType === 'activity') return 'Créer une activité'
  return 'Créer'
}

function formatDisplayValue(value, fields) {
  var matchingField = fields.find(function (field) { return field.proposedValue === value })
  if (matchingField && matchingField.inputType === 'datetime-local') return formatDateTimeLabel(value)
  return value
}

function formatDateTimeLabel(value) {
  var date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value || '')
  return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }) + ' · ' + date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
}

function formatDateTimeLocal(value) {
  if (!value) return ''
  var date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  var offset = date.getTimezoneOffset()
  var local = new Date(date.getTime() - offset * 60000)
  return local.toISOString().slice(0, 16)
}

function formatDateValue(value) {
  if (!value) return ''
  var normalized = String(value).trim()
  var match = /^(\d{4})-(\d{2})-(\d{2})/.exec(normalized)
  if (!match) {
    var date = new Date(normalized)
    if (Number.isNaN(date.getTime())) return ''
    match = [null, String(date.getUTCFullYear()), String(date.getUTCMonth() + 1).padStart(2, '0'), String(date.getUTCDate()).padStart(2, '0')]
  }
  return match[3] + '/' + match[2] + '/' + match[1]
}

function parseDateValue(value) {
  var normalized = String(value || '').trim()
  if (!normalized) return ''
  var match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(normalized)
  if (!match) return normalized
  var day = Number(match[1])
  var month = Number(match[2])
  var year = Number(match[3])
  var date = new Date(Date.UTC(year, month - 1, day))
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return normalized
  return year + '-' + match[2] + '-' + match[1]
}

function normalizeBoolean(value) {
  var normalized = String(value || '').trim().toLowerCase()
  return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'oui'
}

function iconForObjectType(objectType) {
  if (objectType === 'contact') return 'userRound'
  if (objectType === 'entity') return 'building2'
  if (objectType === 'deal') return 'handshake'
  if (objectType === 'activity') return 'calendarCheck'
  return 'box'
}

function renderSourceBadge(source) {
  var label = source === 'user_explicit'
    ? 'Fourni'
    : source === 'context'
      ? 'Contexte'
      : source === 'uncertain'
        ? 'Incertain'
        : 'Déduit'
  return '<span class="wc-dictation-writing-plan-source" data-dictation-source="' + escapeAttributeValue(source) + '">' + escapeHtml(label) + '</span>'
}

function cssEscape(value) {
  return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"')
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function escapeAttributeValue(value) {
  return escapeHtml(value).replace(/`/g, '&#96;')
}

export { bindDictationWritingPlanUi, normalizeDictationWritingPlanUi, renderDictationWritingPlanUi }
